import { trackPromise } from 'react-promise-tracker';
import imageCompression from 'browser-image-compression';
import apiClient from './apiClient';

// ==========================
// HELPERS
// ==========================

async function handleImageUpload(imageFile) {
    const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true
    };

    try {
        return await imageCompression(imageFile, options);
    } catch {
        return imageFile;
    }
}

// ==========================
// USERS - GETS
// ==========================

async function listUsers() {
    const { data } = await trackPromise(apiClient.get('/api/user/employees'));
    return data?.data ?? [];
}

async function listAdmins() {
    const { data } = await trackPromise(apiClient.get('/api/user/admins'));
    return data?.data ?? [];
}

async function getUserById(id) {
    const { data } = await trackPromise(apiClient.get(`/api/user/${id}`));
    return data?.data ?? null;
}

// ==========================
// USERS - MUTATIONS
// ==========================

async function createAdminUser(state) {
    const payload = {
        name: state.name,
        lastname: state.lastName,
        password: state.password,
        username: state.email,
        rol: state.rolSelected,
        factoryId: state.factoryId
    };

    const { data } = await trackPromise(apiClient.post('/api/user/admin/create', payload));

    return data;
}

async function createProductionUser(state) {
    const payload = {
        name: state.name,
        lastname: state.lastName,
        username: state.username,
        rol: state.rolSelected,
        factoryId: state.factoryId,
        shiftId: state.shiftId,
        position: state.position
    };

    const { data } = await trackPromise(apiClient.post('/api/user/production/create', payload));

    return data;
}

async function updateAdminUser(state) {
    const payload = {
        name: state.name,
        lastname: state.lastName,
        password: state.password,
        username: state.email,
        rol: state.rolSelected,
        factoryId: state.factoryId
    };

    const { data } = await trackPromise(apiClient.put(`/api/user/admin/${state.id}`, payload));

    return data;
}

async function updateProductionUser(state) {
    const payload = {
        name: state.name,
        lastname: state.lastName,
        password: state.password,
        username: state.username,
        rol: state.rolSelected,
        factoryId: state.factoryId,
        shiftId: state.shiftId,
        position: state.position
    };

    const { data } = await trackPromise(apiClient.put(`/api/user/production/${state.id}`, payload));

    return data;
}

async function toggleUser(id) {
    const { data } = await trackPromise(apiClient.put(`/api/user/${id}/enabled`));
    return data;
}

// ==========================
// IMPORT CSV
// ==========================

async function importUsers(files) {
    const bodyFormData = new FormData();

    if (files) {
        for (const file of files) {
            const finalFile = await handleImageUpload(file);
            bodyFormData.append('files', finalFile, file.name);
        }
    }

    const { data } = await trackPromise(
        apiClient.post('/api/user/importOptimize', bodyFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    );

    return data;
}

async function getImportProgress(processId) {
    const { data } = await apiClient.get(`/api/user/import/status/${processId}`);

    return data; // { processed, total, errors, finished }
}

async function getNotifications() {
    const { data } = await apiClient.post(`/api/notifications/list`);

    return data; // { processed, total, errors, finished }
}

// ==========================
// EXPORT
// ==========================

const userService = {
    listUsers,
    listAdmins,
    getUserById,

    createAdminUser,
    createProductionUser,
    updateAdminUser,
    updateProductionUser,
    toggleUser,

    importUsers,
    getImportProgress,
    getNotifications
};

export default userService;
